"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ObjectDiscoveryAgent = void 0;
const model_1 = require("../../../data/model");
const linkup_1 = require("../../../net/linkup");
const logging_1 = require("../../../util/logging");
const NetworkAgent_1 = require("../network/NetworkAgent");
const ObjectBroadcastAgent_1 = require("./ObjectBroadcastAgent");
const streams_1 = require("../../../util/streams");
class ObjectDiscoveryAgent {
    constructor(hexHashSuffix, params) {
        this.wasShutdown = false;
        this.hexHashSuffix = hexHashSuffix;
        if (params === undefined) {
            params = {};
        }
        this.params = {
            broadcastedSuffixBits: (params === null || params === void 0 ? void 0 : params.broadcastedSuffixBits) === undefined ? ObjectBroadcastAgent_1.ObjectBroadcastAgent.defaultBroadcastedSuffixBits : params.broadcastedSuffixBits,
            maxQueryFreq: (params === null || params === void 0 ? void 0 : params.maxQueryFreq) === undefined ? 2 : params.maxQueryFreq,
            maxStoredReplies: (params === null || params === void 0 ? void 0 : params.maxStoredReplies) === undefined ? 15 : params.maxStoredReplies
        };
        this.localEndpoints = new Set();
        this.lastQueryingTimePerServer = new Map();
        this.streamSource = new streams_1.BufferingAsyncStreamSource(this.params.maxStoredReplies);
    }
    static agentIdForHexHashSuffix(suffix) {
        return 'object-discovery-for-' + suffix;
    }
    getAgentId() {
        return ObjectDiscoveryAgent.agentIdForHexHashSuffix(this.hexHashSuffix);
    }
    ready(pod) {
        this.pod = pod;
    }
    query(linkupServers, localEndpoint, count = 1) {
        if (this.pod === undefined) {
            throw new Error('This ObjectDiscoveryAgent has not been registered to a mesh so it cannot accept queries yet.');
        }
        if (this.wasShutdown) {
            throw new Error('This ObjectDiscoveryAgent was shut down, it cannot accept more queries.');
        }
        const currentTime = Date.now();
        const request = {
            hashSuffix: this.hexHashSuffix,
            agentId: this.getAgentId()
        };
        if (!this.localEndpoints.has(localEndpoint)) {
            this.getNetworkAgent().listenForLinkupMessages(localEndpoint);
            this.localEndpoints.add(localEndpoint);
        }
        for (const linkupServer of linkupServers) {
            const lastQueryingTime = this.lastQueryingTimePerServer.get(linkupServer);
            if (lastQueryingTime === undefined ||
                currentTime >= lastQueryingTime + this.params.maxQueryFreq * 1000) {
                this.lastQueryingTimePerServer.set(linkupServer, currentTime);
                const broadcasted = ObjectBroadcastAgent_1.ObjectBroadcastAgent.trimHexSuffix(this.hexHashSuffix, this.params.broadcastedSuffixBits);
                ObjectDiscoveryAgent.log.trace(() => 'Sending peer query from endpoint ' +
                    localEndpoint +
                    ' to endpoint ' +
                    new linkup_1.LinkupAddress(linkupServer, ObjectBroadcastAgent_1.ObjectBroadcastAgent.linkupIdForHexHashSuffix(broadcasted)).url() +
                    ' for suffix ' + this.hexHashSuffix);
                this.getNetworkAgent().sendLinkupMessage(linkup_1.LinkupAddress.fromURL(localEndpoint), new linkup_1.LinkupAddress(linkupServer, ObjectBroadcastAgent_1.ObjectBroadcastAgent.linkupIdForHexHashSuffix(broadcasted)), ObjectBroadcastAgent_1.ObjectBroadcastAgent.agentIdForHexHashSuffix(this.hexHashSuffix, this.params.broadcastedSuffixBits), request, Math.ceil(count * 1.5));
            }
            else {
                ObjectDiscoveryAgent.log.trace(() => 'Object discovery query ignored for server ' + linkupServer + ', we queried too recently there.');
            }
        }
    }
    getReplyStream(filterParams) {
        let source = this.streamSource;
        const maxAge = filterParams === null || filterParams === void 0 ? void 0 : filterParams.maxAge;
        const linkupServers = filterParams === null || filterParams === void 0 ? void 0 : filterParams.linkupServers;
        const localEndpoints = filterParams === null || filterParams === void 0 ? void 0 : filterParams.localEndpoints;
        if (maxAge !== undefined ||
            linkupServers !== undefined ||
            localEndpoints !== undefined) {
            let filter = (elem) => {
                let now = Date.now();
                let accept = true;
                accept = accept && (maxAge === undefined || elem.timestamp >= now - maxAge * 1000);
                accept = accept && (linkupServers === undefined || linkupServers.indexOf(linkup_1.LinkupAddress.fromURL(elem.source).serverURL) >= 0);
                accept = accept && (localEndpoints === undefined || localEndpoints.indexOf(elem.destination) >= 0);
                return accept;
            };
            source = new streams_1.FilteredAsyncStreamSource(source, filter);
        }
        return new streams_1.BufferedAsyncStream(source);
        ;
    }
    receiveLocalEvent(ev) {
        if (!this.wasShutdown && ev.type === NetworkAgent_1.NetworkEventType.LinkupMessageReceived) {
            const msg = ev.content;
            if (msg.agentId === this.getAgentId()) {
                const reply = msg.content;
                let replyHash = '';
                let object = undefined;
                try {
                    object = model_1.HashedObject.fromLiteralContext(reply.literalContext);
                    replyHash = object.hash();
                }
                catch (e) {
                    ObjectDiscoveryAgent.log.warning('Error deliteralizing object discovery reply:' + e);
                    object = undefined;
                }
                if (object !== undefined && replyHash === reply.literalContext.rootHashes[0] &&
                    this.hexHashSuffix === model_1.Hashing.toHex(replyHash).slice(-this.hexHashSuffix.length) &&
                    this.localEndpoints.has(msg.destination)) {
                    ObjectDiscoveryAgent.log.trace(() => 'Received object with hash ' + replyHash + ' from ' + msg.source + ' at ' + msg.destination);
                    let item = { source: msg.source, destination: msg.destination, hash: replyHash, object: object, timestamp: Date.now() };
                    this.streamSource.ingest(item);
                }
                else {
                    ObjectDiscoveryAgent.log.debug('Error validating object discovery reply');
                }
            }
        }
    }
    shutdown() {
        // TODO: stop listening on linkup endpoints
        this.wasShutdown = true;
        this.streamSource.end();
    }
    getNetworkAgent() {
        var _a;
        return (_a = this.pod) === null || _a === void 0 ? void 0 : _a.getAgent(NetworkAgent_1.NetworkAgent.AgentId);
    }
}
exports.ObjectDiscoveryAgent = ObjectDiscoveryAgent;
ObjectDiscoveryAgent.log = new logging_1.Logger(ObjectDiscoveryAgent.name, logging_1.LogLevel.INFO);
ObjectDiscoveryAgent.newestReplyFirst = (a, b) => (b.timestamp - a.timestamp);
//# sourceMappingURL=ObjectDiscoveryAgent.js.map